import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import {
  ControlComponent,
  GeoJSONSourceComponent,
  LayerComponent,
  MapComponent,
  MarkerComponent,
  PopupComponent,
} from '@maplibre/ngx-maplibre-gl';
import { mapStyleMap, MapStyleType, MAPTILES_KEY } from '@wheere-front/shared-map-libre';
import { SwitchComponent } from '@wheere-front/shared-ui';
import { Position } from '@wheere/api-tracker';
import dayjs from 'dayjs';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { Map as MlMap, MapMouseEvent } from 'maplibre-gl';

/** One tracker's path on the map: its identity, a display colour and its raw positions. */
export interface TrajectoryTrack {
  id: string;
  name: string;
  color: string;
  positions: Position[];
}

/** A fixed gateway shown as context on the map (the gateways of the plotted trackers' sites). */
export interface GatewayMarker {
  id: string;
  name: string;
  lng: number;
  lat: number;
}

/** Per-track render data: visible, valid, time-sorted points (lng/lat/epoch-ms). */
interface PreparedTrack {
  id: string;
  name: string;
  color: string;
  pts: { lng: number; lat: number; ts: number }[];
}

/** Tooltip shown while hovering a point. */
interface Hovered {
  lng: number;
  lat: number;
  name: string;
  label: string;
  color: string;
}

const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

/** Default map view (Paris) when no track has plottable points yet. */
const DEFAULT_BOUNDS: [[number, number], [number, number]] = [
  [2.2241, 48.8156],
  [2.4699, 48.9022],
];

/**
 * Multi-tracker trajectory map. Draws one coloured polyline + points per visible {@link
 * TrajectoryTrack}, auto-fits the view over every plotted point, and shows a date popup on hover.
 *
 * <p>Two optional, page-driven modes layer on top:
 * <ul>
 *   <li>{@link hiddenIds} — tracks to hide (clickable legend lives in the page).</li>
 *   <li>{@link currentTime} — a shared playback clock (epoch ms): when set, the full paths fade,
 *       the travelled portion of each track up to that instant is highlighted, and a marker shows
 *       each tracker's position at that moment. {@code null} = static full paths.</li>
 * </ul>
 *
 * Read-only and self-contained (no shared map state) so several can coexist with the single-tracker
 * history map.
 */
@Component({
  selector: 'lib-map-trajectories',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MapComponent,
    ControlComponent,
    GeoJSONSourceComponent,
    LayerComponent,
    MarkerComponent,
    PopupComponent,
    SwitchComponent,
  ],
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        width: 100%;
      }
    `,
  ],
  template: `
    <div class="relative h-full w-full">
      <mgl-map
        [mapStyle]="mapStyle()"
        [fitBounds]="fitBounds()"
        [fitBoundsOptions]="{ padding: 48, maxZoom: 17 }"
        [maxZoom]="19"
        (mapLoad)="onMapLoad($event)"
        class="h-full w-full">
        <mgl-control position="bottom-left">
          <lib-switch [checked]="mapStyleType() !== 'street'" (changed)="onToggleMapStyle()" />
        </mgl-control>

        <!-- Full paths (faded during playback). -->
        <mgl-geojson-source id="traj-full" [data]="fullLines()" />
        <mgl-layer
          id="traj-full-line"
          source="traj-full"
          type="line"
          [layout]="{ 'line-cap': 'round', 'line-join': 'round' }"
          [paint]="{ 'line-color': ['get', 'color'], 'line-width': 3, 'line-opacity': fullLineOpacity() }" />

        <!-- Travelled portion up to the playback cursor. -->
        <mgl-geojson-source id="traj-travelled" [data]="travelledLines()" />
        <mgl-layer
          id="traj-travelled-line"
          source="traj-travelled"
          type="line"
          [layout]="{ 'line-cap': 'round', 'line-join': 'round' }"
          [paint]="{ 'line-color': ['get', 'color'], 'line-width': 4, 'line-opacity': 0.95 }" />

        <!-- All points — hover shows the date. -->
        <mgl-geojson-source id="traj-points" [data]="pointsFc()" />
        <mgl-layer
          id="traj-points-layer"
          source="traj-points"
          type="circle"
          [paint]="{
            'circle-radius': 4,
            'circle-color': ['get', 'color'],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
          }"
          (layerMouseMove)="onHover($event)"
          (layerMouseLeave)="onLeave()" />

        <!-- Current position per track during playback (halo + dot). -->
        <mgl-geojson-source id="traj-current" [data]="currentMarkers()" />
        <mgl-layer
          id="traj-current-halo"
          source="traj-current"
          type="circle"
          [paint]="{ 'circle-radius': 12, 'circle-color': ['get', 'color'], 'circle-opacity': 0.25 }" />
        <mgl-layer
          id="traj-current-dot"
          source="traj-current"
          type="circle"
          [paint]="{
            'circle-radius': 6,
            'circle-color': ['get', 'color'],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          }" />

        <!-- Gateways of the plotted trackers' sites — fixed context markers. -->
        @for (g of gateways(); track g.id) {
          <mgl-marker [lngLat]="[g.lng, g.lat]">
            <span
              class="flex size-6 items-center justify-center rounded-full border-2 border-white shadow-md"
              style="background:#ffc800"
              [title]="g.name">
              <svg class="size-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 12a7 7 0 0 1 14 0M8.5 12a3.5 3.5 0 0 1 7 0M12 12v8" />
              </svg>
            </span>
          </mgl-marker>
        }

        @if (hovered(); as h) {
          <mgl-popup [lngLat]="[h.lng, h.lat]" [closeButton]="false" [closeOnClick]="false" [offset]="12">
            <div class="px-0.5 text-sm leading-tight">
              <div class="font-semibold" [style.color]="h.color">{{ h.name }}</div>
              <div class="text-gray-600">{{ h.label }}</div>
            </div>
          </mgl-popup>
        }
      </mgl-map>
    </div>
  `,
})
export class MapTrajectoriesComponent {
  /** Tracks to plot. Each visible one gets its own coloured polyline + points. */
  readonly tracks = input<TrajectoryTrack[]>([]);

  /** Ids hidden by the legend toggle. */
  readonly hiddenIds = input<string[]>([]);

  /** Playback cursor (epoch ms); {@code null} = static full paths. */
  readonly currentTime = input<number | null>(null);

  /** Gateways (of the plotted trackers' sites) shown as fixed context markers. */
  readonly gateways = input<GatewayMarker[]>([]);

  // Self-contained map style (street/satellite) — kept local so several maps don't share state.
  #key = inject(MAPTILES_KEY, { optional: true }) ?? '';
  protected readonly mapStyleType = signal<MapStyleType>('street');
  protected readonly mapStyle = computed(() => mapStyleMap(this.#key)[this.mapStyleType()]);

  #map = signal<MlMap | null>(null);
  protected readonly hovered = signal<Hovered | null>(null);

  protected onToggleMapStyle(): void {
    this.mapStyleType.update(t => (t === 'street' ? 'satellite' : 'street'));
  }

  protected onMapLoad(map: MlMap | undefined): void {
    this.#map.set(map ?? null);
  }

  /** Visible tracks with valid, time-sorted points. Independent of the playback cursor so it isn't
   * rebuilt every animation tick. */
  protected readonly prepared = computed<PreparedTrack[]>(() => {
    const hidden = new Set(this.hiddenIds());
    return this.tracks()
      .filter(t => !hidden.has(t.id))
      .map(t => ({
        id: t.id,
        name: t.name,
        color: t.color,
        pts: t.positions
          .filter(MapTrajectoriesComponent.isPlottable)
          .map(p => ({ lng: p.longitude as number, lat: p.latitude as number, ts: MapTrajectoriesComponent.ts(p) }))
          .sort((a, b) => a.ts - b.ts),
      }));
  });

  protected readonly fullLineOpacity = computed(() => (this.currentTime() != null ? 0.25 : 0.85));

  protected readonly fullLines = computed<FeatureCollection>(() => ({
    type: 'FeatureCollection',
    features: this.prepared()
      .filter(t => t.pts.length >= 2)
      .map(t => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: t.pts.map(p => [p.lng, p.lat]) },
        properties: { color: t.color },
      })),
  }));

  protected readonly pointsFc = computed<FeatureCollection>(() => ({
    type: 'FeatureCollection',
    features: this.prepared().flatMap(t =>
      t.pts.map(
        (p): Feature => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
          properties: { color: t.color, name: t.name, label: dayjs(p.ts).format('DD/MM/YYYY HH:mm:ss') },
        })
      )
    ),
  }));

  protected readonly travelledLines = computed<FeatureCollection>(() => {
    const ct = this.currentTime();
    if (ct == null) return EMPTY_FC;
    const features: Feature[] = [];
    for (const t of this.prepared()) {
      const coords = t.pts.filter(p => p.ts <= ct).map(p => [p.lng, p.lat]);
      if (coords.length >= 2) {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords },
          properties: { color: t.color },
        });
      }
    }
    return { type: 'FeatureCollection', features };
  });

  protected readonly currentMarkers = computed<FeatureCollection>(() => {
    const ct = this.currentTime();
    if (ct == null) return EMPTY_FC;
    const features: Feature[] = [];
    for (const t of this.prepared()) {
      let last: { lng: number; lat: number } | null = null;
      for (const p of t.pts) {
        if (p.ts <= ct) last = p;
        else break;
      }
      if (last) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [last.lng, last.lat] },
          properties: { color: t.color, name: t.name },
        });
      }
    }
    return { type: 'FeatureCollection', features };
  });

  protected readonly fitBounds = computed<[[number, number], [number, number]]>(() => {
    const lngs: number[] = [];
    const lats: number[] = [];
    for (const t of this.prepared()) {
      for (const p of t.pts) {
        lngs.push(p.lng);
        lats.push(p.lat);
      }
    }
    for (const g of this.gateways()) {
      lngs.push(g.lng);
      lats.push(g.lat);
    }
    if (!lngs.length) return DEFAULT_BOUNDS;
    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
  });

  protected onHover(e: MapMouseEvent & { features?: Feature[] }): void {
    const f = e.features?.[0];
    if (!f || f.geometry.type !== 'Point') return;
    const [lng, lat] = (f.geometry as Point).coordinates as [number, number];
    const props = (f.properties ?? {}) as { color?: string; name?: string; label?: string };
    this.hovered.set({ lng, lat, name: props.name ?? '', label: props.label ?? '', color: props.color ?? '#000' });
    const m = this.#map();
    if (m) m.getCanvas().style.cursor = 'pointer';
  }

  protected onLeave(): void {
    this.hovered.set(null);
    const m = this.#map();
    if (m) m.getCanvas().style.cursor = '';
  }

  private static isPlottable(p: Position): boolean {
    return (
      typeof p.longitude === 'number' &&
      typeof p.latitude === 'number' &&
      !isNaN(p.longitude) &&
      !isNaN(p.latitude) &&
      p.longitude !== 0 &&
      p.latitude !== 0
    );
  }

  private static ts(p: Position): number {
    return p.timestamp ? new Date(p.timestamp).getTime() : 0;
  }
}
